import { useEffect, useState } from 'react';

const VERSION_URL = '/version.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const POLL_JITTER_MS = 90 * 1000;

const BUILD_VERSION = process.env.REACT_APP_GIT_SHA || 'dev';

// Rotas onde recarregar custa caro. src/pages/services/ServiceForm mantem um lock de
// rascunho que e liberado no beforeunload via fetch(keepalive) (useServiceDraft.ts:558) e
// um autosave com debounce de 1.5s cujo flush NAO roda em reload. Como o lock e liberado
// e readquirido por duas requisicoes sem ordem garantida, um reload pode devolver o
// usuario com o lock solto. Nessas rotas o aviso fica suprimido; ele aparece quando o
// usuario sair da tela.
// Cobre /services/create e /services/:serviceId, sem pegar a listagem /services.
const SUPPRESSED_PREFIXES = ['/services/'];

/**
 * Compara o SHA compilado no bundle com o /version.json publicado pelo nginx.
 *
 * Apenas DETECTA: nao recarrega nada. Quem decide e o usuario, clicando no banner. Isso
 * elimina por construcao o risco de loop de reload -- se por qualquer motivo o bundle e o
 * version.json divergirem de forma permanente, o pior caso e um banner que nao some, e
 * nao uma aba que recarrega sem parar.
 */
const useVersionCheck = (pathname?: string): boolean => {
  const [hasNewVersion, setHasNewVersion] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return undefined;
    // Build sem SHA injetado (ex.: build local, ou --build-arg perdido no CI): sem
    // baseline confiavel qualquer comparacao seria ruido. Degrada para no-op.
    if (BUILD_VERSION === 'dev') return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const check = async () => {
      try {
        // cache:'no-store' cobre o browser; o cache-buster cobre qualquer intermediario.
        const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled || !data || !data.version) return;
        if (data.version !== BUILD_VERSION) setHasNewVersion(true);
      } catch (e) {
        // offline, deploy em andamento, ou version.json ainda inexistente (o container
        // reinicia durante o deploy). Tenta de novo no proximo ciclo.
      }
    };

    // Jitter: o deploy e um evento sincronizador. Sem ele todas as abas checariam juntas
    // logo depois que o container sobe.
    const schedule = () => {
      timer = setTimeout(() => {
        check().then(() => {
          if (!cancelled) schedule();
        });
      }, POLL_INTERVAL_MS + Math.floor(Math.random() * POLL_JITTER_MS));
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };

    check();
    schedule();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  if (!hasNewVersion) return false;
  if (pathname && SUPPRESSED_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return false;
  }
  return true;
};

export default useVersionCheck;
